<script>
	const { events } = require('utilities/analyticsUtilities');
	const _ = require('underscore');
	const config = require('core/config');

	const WORDSMYTH_BASE = (config.resources.media.cdn || config.resources.media.origin) + '/ws/';
	const WORDSMYTH_IMAGE_BASE = WORDSMYTH_BASE + 'image/';
	const WORDSMYTH_SOUND_BASE = WORDSMYTH_BASE + 'sound/wedt/';

	module.exports = {
		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'outbound-link': require('components/outbound-link.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default
		},
		props: {
			showAudio: {
				type: Boolean,
				default: false
			},
			article: {
				type: Object,
				required: true
			},
			senseId: {
				required: true
			},
			occurrenceId: {
				required: false
			},
			eventData: {
				required: false,
				default: function () {
					return {
						parentPid: null,
						assignmentId: null
					};
				}
			}
		},
		modelData: ['offlineMode'],
		data() {
			return {
				events
			};
		},
		methods: {
			audioEventDataFor(loc) {
				return _.assign({}, this.audioEventData, { loc: 'vocab_modal_' + loc });
			}
		},
		computed: {
			audioEventData() {
				return {
					parentPid: this.eventData.parentPid || undefined,
					pid: this.article.uuid,
					aid: this.eventData.assignmentId || undefined,
					w: (this.sense && this.sense.word) || undefined,
					senseId: parseInt(this.senseId)
				};
			},
			vocab() {
				return this.article.vocab;
			},
			sense() {
				return this.vocab && this.vocab.support.senses[this.senseId];
			},
			imageUrl() {
				if (this.sense && this.sense.image) {
					return WORDSMYTH_IMAGE_BASE + this.sense.image;
				} else {
					return null;
				}
			},
			audioUrl() {
				if (this.sense && this.sense.pronunciation) {
					return WORDSMYTH_SOUND_BASE + this.sense.pronunciation;
				} else {
					return null;
				}
			},
			spanish() {
				if (this.sense.spanish) {
					return this.sense.spanish.join(', ');
				} else {
					return '';
				}
			},
			chinese() {
				if (this.sense.chinese) {
					return this.sense.chinese.join(', ');
				} else {
					return '';
				}
			},
			occurrence() {
				return this.occurrenceId && this.vocab.support.occurrences[this.occurrenceId];
			},
			partsOfSpeech() {
				return this.sense.part_of_speech.join(' & ');
			}
		}
	};
</script>
<template>
	<div
		v-if="sense"
		class="vocab-entry">
		<div class="headword_block">
			<span class="headword">{{ sense.word }}</span>
			<audio-player-simple
				v-if="offlineMode === false && showAudio && audioUrl"
				:url="audioUrl"
				:event-data="audioEventData"
				:audio-play-event-name="events.WORDSMYTH_AUDIO_PLAY"
				:audio-pause-event-name="events.WORDSMYTH_AUDIO_PAUSE"
				:audio-complete-event-name="events.WORDSMYTH_AUDIO_COMPLETE">
			</audio-player-simple>
		</div>
		<div class="pos_block">
			<span class="pos_title">part of speech:</span>
			<span class="pos_value">{{ partsOfSpeech }}</span>
		</div>
		<div class="key-value-block">
			<span class="key">definition:</span>
			<span class="value">
				{{ sense.definition }}
				<text-to-speech-player-simple
					v-if="offlineMode === false && showAudio"
					:text="sense.definition"
					:event-data="audioEventDataFor('definition')">
				</text-to-speech-player-simple>
			</span>
		</div>

		<div
			class="key-value-block"
			v-for="example in sense.example_list"
			:key="example">
			<span class="value example-value">
				{{ example }}
				<text-to-speech-player-simple
					v-if="offlineMode === false && showAudio"
					:text="example"
					:event-data="audioEventDataFor('example')">
				</text-to-speech-player-simple>
			</span>
		</div>

		<div
			class="key-value-block"
			v-if="occurrence && occurrence.text">
			<span class="value occurrence-value">
				{{ occurrence.text }}
				<text-to-speech-player-simple
					v-if="offlineMode === false && showAudio"
					:text="occurrence.text"
					:event-data="audioEventDataFor('occurrence')">
				</text-to-speech-player-simple>
			</span>
		</div>

		<div
			class="key-value-block"
			v-if="offlineMode === false && imageUrl">
			<span class="value">
				<img
					class="vocab-entry-image"
					:src="imageUrl" />
			</span>
		</div>

		<div
			class="key-value-block"
			v-if="spanish">
			<span class="key">Spanish:</span>
			<span
				class="value foreign-language-value"
				v-html="spanish"></span>
		</div>

		<div
			class="key-value-block chinese-key-value"
			v-if="chinese">
			<span class="key">Chinese:</span>
			<span
				class="value foreign-language-value chinese-language"
				v-html="chinese"></span>
		</div>

		<div
			class="key-value-block"
			v-if="sense.inflection_list && sense.inflection_list.length">
			<span class="key">forms:</span>
			<span class="value">{{ sense.inflection_list.join(', ') }}</span>
		</div>
	</div>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.vocab-entry {
		color: black;
		font-family: var(--custom-font-family, $helvetica);
		break-inside: avoid;
		page-break-inside: avoid;

		.key-value-block {
			margin-top: 0.3rem;
			padding-left: 5.5rem;
			.key,
			.value {
				display: block;
			}
			.key {
				float: left;
				margin-left: -5.5rem;
			}
			.value img {
				max-height: 150px;
				max-width: 250px;
			}
		}

		.value.example-value {
			font-style: italic;
		}

		.value.occurrence-value {
			font-style: italic;
			color: $c-blue;
		}

		.value.foreign-language-value {
			color: $c-slate-dark;
		}

		div.headword_block {
			margin-bottom: 2em;
		}

		span.headword {
			font-size: 150%;
			font-weight: 700;
			color: $c-blue;
		}

		div.pos_block {
			background-color: #f6f3a6;
		}

		span.occurrence {
			text-decoration: underline;
			background-color: #f2f2f2;
		}

		div.headword_block {
			margin-bottom: 0;
		}

		div.pos_block {
			background-color: white;
			margin-top: 0.3em;
			padding-bottom: 0.3em;
		}

		span.pos_title {
			display: none;
		}

		span.pos_value {
			color: $c-blue-dark;
		}

		span.pos_title {
			padding-left: 0;
			padding-right: 0.5em;
			min-width: 4.5em;
		}
	}
</style>
